import {
  DirectionsRenderer,
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { serverUrl } from "../../../../nestserver";
import { message } from "antd";

const LIBRARIES = ["places"];

const LiveBusMap = ({ routeData, routeId, routes = [] }) => {
  const mapRef = useRef();
  const socketRef = useRef(null);
  const [currentLocations, setCurrentLocations] = useState({});
  const [activeSessions, setActiveSessions] = useState([]);
  const [error, setError] = useState(null);
  const [socket, setSocket] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 28.7041,
    lng: 77.1025,
  });
  const [isConnected, setIsConnected] = useState(false);
  const [selectedBus, setSelectedBus] = useState(null);
  const [directionResponse, setDirectionResponse] = useState(null);

  const mapContainerStyle = {
    width: "100%",
    height: "75vh",
  };

  // Connect to WebSocket
  useEffect(() => {
    const token = window.localStorage.getItem("jwt-token");
    if (!token) return;

    if (!routeId && activeSessions.length === 0) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Avoid multiple connection attempts
    if (
      socketRef.current &&
      (socketRef.current.connected || socketRef.current.connecting)
    )
      return;

    const newSocket = io(`${serverUrl}/location`, {
      auth: { token },
      transports: ["websocket"],
      reconnectionAttempts: 3,
      timeout: 10000,
    });

    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      console.log("Connected to location socket");
      setIsConnected(true);
      setError(null);
    });

    newSocket.on("connect_error", (err) => {
      console.error("Socket connection error:", err.message);
      if (routeId || activeSessions.length > 0) {
        setError("Waiting for active tracking...");
      }
      setIsConnected(false);
    });

    newSocket.on("disconnect", (reason) => {
      console.log("Disconnected from location socket:", reason);
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        newSocket.disconnect();
        socketRef.current = null;
      }
    };
  }, [routeId, activeSessions.length]);

  // Fetch active sessions to know which routes to subscribe to (Fleet mode)
  useEffect(() => {
    const fetchActiveSessions = async () => {
      try {
        const res = await fetch(`${serverUrl}/location/sessions/active`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        });
        const data = await res.json();
        if (data?.result?.data) {
          setActiveSessions(data.result.data);
        }
      } catch (err) {
        console.error("Failed to fetch active sessions", err);
      }
    };

    fetchActiveSessions();
    const interval = setInterval(fetchActiveSessions, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  // Handle subscriptions
  useEffect(() => {
    if (!socket || !isConnected) return;

    const subscribeToRoute = (id) => {
      socket.emit("viewer:subscribe-route", { route_id: id }, (res) => {
        if (res.success) console.log("Subscribed to route:", id);
      });
    };

    if (routeId) {
      // Single route mode
      subscribeToRoute(routeId);
    } else if (activeSessions.length > 0) {
      // Fleet mode: Subscribe to all active routes in school
      activeSessions.forEach((s) =>
        subscribeToRoute(s.route_id?._id || s.route_id),
      );
    }

    const handleLocationUpdate = (data) => {
      const rid = data.route_id;
      const newLoc = {
        lat: data.latitude,
        lng: data.longitude,
        speed: data.speed,
        timestamp: data.timestamp,
        route_id: rid,
        name: routes.find((r) => r._id === rid)?.name || "Bus",
      };

      setCurrentLocations((prev) => ({
        ...prev,
        [rid]: newLoc,
      }));

      // Auto-pan if it's the only route or the selected route
      if (routeId === rid || (!routeId && selectedBus?.route_id === rid)) {
        if (mapRef.current) mapRef.current.panTo(newLoc);
      }
    };

    socket.on("location:update", handleLocationUpdate);
    socket.on("session:ended", (data) => {
      setCurrentLocations((prev) => {
        const next = { ...prev };
        delete next[data.route_id];
        return next;
      });
      if (routeId === data.route_id) {
        message.info("Tracking session has ended");
      }
    });

    return () => {
      if (routeId) {
        socket.emit("viewer:unsubscribe-route", { route_id: routeId });
      } else {
        activeSessions.forEach((s) =>
          socket.emit("viewer:unsubscribe-route", {
            route_id: s.route_id?._id || s.route_id,
          }),
        );
      }
      socket.off("location:update");
      socket.off("session:ended");
    };
  }, [socket, isConnected, routeId, activeSessions, routes, selectedBus]);

  // Map Centering
  useEffect(() => {
    if (routeData?.start_point) {
      setMapCenter({
        lat: routeData.start_point.latitude,
        lng: routeData.start_point.longitude,
      });
    } else if (routeData?.source_location?.coordinates) {
      setMapCenter({
        lat: routeData.source_location.coordinates[1],
        lng: routeData.source_location.coordinates[0],
      });
    }
  }, [routeData]);

  // Compute road-following directions whenever routeData changes
  useEffect(() => {
    setDirectionResponse(null);

    const startLat =
      routeData?.start_point?.latitude ||
      routeData?.source_location?.coordinates?.[1];
    const startLng =
      routeData?.start_point?.longitude ||
      routeData?.source_location?.coordinates?.[0];
    const endLat =
      routeData?.end_point?.latitude ||
      routeData?.destination_location?.coordinates?.[1];
    const endLng =
      routeData?.end_point?.longitude ||
      routeData?.destination_location?.coordinates?.[0];

    if (!startLat || !startLng || !endLat || !endLng) return;

    const waypoints = [];
    if (routeData?.pickup_points?.length > 0) {
      [...routeData.pickup_points]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .forEach((p) =>
          waypoints.push({
            location: new window.google.maps.LatLng(p.latitude, p.longitude),
            stopover: true,
          }),
        );
    } else if (routeData?.route_map?.length > 2) {
      routeData.route_map.slice(1, -1).forEach((p) => {
        if (p.coordinates) {
          waypoints.push({
            location: new window.google.maps.LatLng(
              p.coordinates[1],
              p.coordinates[0],
            ),
            stopover: true,
          });
        }
      });
    }

    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin: new window.google.maps.LatLng(startLat, startLng),
        destination: new window.google.maps.LatLng(endLat, endLng),
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") setDirectionResponse(result);
      },
    );
  }, [routeData]);

  return (
    <div>
      {error && <div className="text-red-500 mb-2">{error}</div>}
      <div className="mb-2">
        {isConnected ? (
          <span className="text-green-500 font-bold">● Live Tracking</span>
        ) : (
          <span className="text-gray-400 italic">Connecting...</span>
        )}
      </div>

      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
        libraries={LIBRARIES}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={13}
          onLoad={(map) => (mapRef.current = map)}
        >
          {/* Static Route Points */}
          {(routeData?.start_point || routeData?.source_location) && (
            <Marker
              position={{
                lat:
                  routeData.start_point?.latitude ||
                  routeData.source_location?.coordinates?.[1],
                lng:
                  routeData.start_point?.longitude ||
                  routeData.source_location?.coordinates?.[0],
              }}
              label={{ text: "S", color: "white" }}
            />
          )}
          {(routeData?.end_point || routeData?.destination_location) && (
            <Marker
              position={{
                lat:
                  routeData.end_point?.latitude ||
                  routeData.destination_location?.coordinates?.[1],
                lng:
                  routeData.end_point?.longitude ||
                  routeData.destination_location?.coordinates?.[0],
              }}
              label={{ text: "E", color: "white" }}
            />
          )}

          {routeData?.pickup_points?.length > 0
            ? routeData.pickup_points.map((p, i) => (
                <Marker
                  key={i}
                  position={{ lat: p.latitude, lng: p.longitude }}
                  label={{ text: `${i + 1}`, color: "white", fontSize: "10px" }}
                />
              ))
            : routeData?.route_map?.length > 2
              ? routeData.route_map.slice(1, -1).map((p, i) => (
                  <Marker
                    key={i}
                    position={{
                      lat: p.coordinates?.[1] || 0,
                      lng: p.coordinates?.[0] || 0,
                    }}
                    label={{
                      text: `${i + 1}`,
                      color: "white",
                      fontSize: "10px",
                    }}
                  />
                ))
              : null}

          {/* Active Buses */}
          {Object.values(currentLocations)
            .filter((loc) => !routeId || loc.route_id === routeId)
            .map((loc) => (
              <Marker
                key={loc.route_id}
                position={loc}
                icon={{
                  url: "https://maps.google.com/mapfiles/kml/shapes/bus.png",
                  scaledSize: new window.google.maps.Size(25, 25),
                }}
                onClick={() => setSelectedBus(loc)}
              />
            ))}

          {/* Info Window for Selected Bus */}
          {selectedBus && (
            <InfoWindow
              position={selectedBus}
              onCloseClick={() => setSelectedBus(null)}
            >
              <div className="p-2">
                <h4 className="font-bold border-b mb-1">{selectedBus.name}</h4>
                <p className="text-sm">
                  <b>Speed:</b> {selectedBus.speed || 0} km/h
                </p>
                <p className="text-xs text-gray-500">
                  <b>Last Updated:</b>{" "}
                  {new Date(selectedBus.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </InfoWindow>
          )}

          {directionResponse && (
            <DirectionsRenderer
              directions={directionResponse}
              options={{ suppressMarkers: true }}
            />
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default LiveBusMap;
